'use client';

import { env } from '@prismo-io/env';
import type { StaticImport } from 'next/dist/shared/lib/get-img-props';
import { type ImageLoaderProps, default as NextImage } from 'next/image';
import {
  type ComponentProps,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react';

const isValidUrl = (toCheck: string): boolean => {
  try {
    const url = new URL(toCheck);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

export const ImageLoader = (bucket = 'avatars') => {
  return (props: ImageLoaderProps) => {
    const { src, width, quality = 75 } = props;

    if (isValidUrl(src)) {
      return src;
    }

    const url = new URL(
      `/api/storage/v1/object/public/${bucket}/${src}`,
      env.NEXT_PUBLIC_BASE_API_URL
    );

    url.searchParams.set('width', width.toString());
    url.searchParams.set('quality', quality.toString());

    return url.toString();
  };
};

type SupabaseImageProps = Omit<
  ComponentProps<typeof NextImage>,
  'loader' | 'src'
> & {
  bucket?: string;
  src?: string | StaticImport | null;
  replaceOnError?: ReactNode;
  Wrapper?: FC<PropsWithChildren>;
};

export const SupabaseImage: FC<SupabaseImageProps> = (props) => {
  const {
    bucket,
    replaceOnError = undefined,
    Wrapper = ({ children }) => children,
    src,
    ...rest
  } = props;

  const [isError, setIsError] = useState<boolean>(false);

  if (!src || (isError && replaceOnError)) {
    return replaceOnError;
  }

  return (
    <Wrapper>
      <NextImage
        {...rest}
        src={src}
        loader={ImageLoader(bucket)}
        onError={() => setIsError(true)}
      />
    </Wrapper>
  );
};
